import { InternalPatientResponse } from "@/Common/Api/ExternalCommunications/Dto/Responses/InternalPatientResponse";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { PatientProfileImagePopup } from "@/Common/Components/Person/ProfileImagePopup";
import { DateTime } from "@/Common/Helpers/DateTime";
import { EmployeeType } from "@/Common/Models/Employee/Enums/EmployeeType";
import { EnumMyleLoginPermission } from "@/Common/Models/EnumMyleLoginPermission";
import { Guid } from "@/Common/Models/Guid";
import { MedicalItemsTab, PatientTab as MyleEnumsPatientPatientTab, PatientTab as PatientTabEnum, UnitSystem } from "@/Common/Models/Patient/PatientEnums";
import { Person } from "@/Common/Models/Person/Person";
import { SecondScreenOperation } from "@/Common/Models/SecondScreen/Enum/SecondScreenOperation";
import { SecondScreenNoteData } from "@/Common/Models/SecondScreenNoteData";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { CanadaState } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";
import { SecondScreenNoteService } from "@/Common/Services/SecondScreenNoteService";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { ExternalMessageFormPopupComponent } from "@/Communications/Components/ExternalCommunications/Form/ExternalMessageFormPopupComponent";
import { FlowsheetModule } from "@/Flowsheets/Components/FlowsheetModule/FlowsheetModule";
import { EmployeeContextProvider } from "@/Flowsheets/Contexts/EmployeeContext";
import { Appointment } from "@/Patient/Components/Appointment/Appointment";
import { EmptyTab } from "@/Patient/Components/EmptyTab";
import { HistoryView } from "@/Patient/Components/History/HistoryView";
import { PatientLabsTab } from "@/Patient/Components/Labs/PatientLabsTab";
import { BaseMedicalItemsView } from "@/Patient/Components/MedicalItems/BaseMedicalItemsView";
import { MedicalItemsActiveMedicationView } from "@/Patient/Components/MedicalItems/MedicalItemsActiveMedication";
import { MedicalItemsAlertView } from "@/Patient/Components/MedicalItems/MedicalItemsAlert";
import { MedicalItemsFamilyHistoryView } from "@/Patient/Components/MedicalItems/MedicalItemsFamilyHistory";
import { MedicalItemsImmunizationView } from "@/Patient/Components/MedicalItems/MedicalItemsImmunization";
import { MedicalItemsMedicalProblemView } from "@/Patient/Components/MedicalItems/MedicalItemsMedicalProblem";
import { MedicalItemsRiskFactorView } from "@/Patient/Components/MedicalItems/MedicalItemsRiskFactor";
import { MedicalItemsScreeningTestView } from "@/Patient/Components/MedicalItems/MedicalItemsScreeningTest";
import { MedicalItemsMedicalReactionView } from "@/Patient/Components/MedicalItems/Reactions/MedicalItemsMedicalReaction";
import { MedicalNoteAlreadyAprovedWarningPopup } from "@/Patient/Components/MedicalNote/MedicalNoteAlreadyAprovedWarningPopup";
import { MedicalNoteDeletedWarningPopup } from "@/Patient/Components/MedicalNote/MedicalNoteDeletedWarningPopup";
import { MedicalSummary } from "@/Patient/Components/MedicalSummary/MedicalSummary";
import { PatientPrescriptionTab } from "@/Patient/Components/Medication/PatientPrescriptionTab";
import { MedicationSearchListItem } from "@/Patient/Components/Medication/SearchMedication";
import { MarkTestPatientPopup } from "@/Patient/Components/PatientBanner/MarkTestPatientPopup";
import { PatientDeactivatePopPopup } from "@/Patient/Components/PatientBanner/PatientDeactivatePopup";
import { PatientMemoPopup } from "@/Patient/Components/PatientBanner/PatientMemoPopup";
import { MedicalSummaryConfigurationHelper } from "@/Patient/Helpers/MedicalSummaryConfigurationHelper";
import { PatientMedicalSummaryHelper } from "@/Patient/Helpers/PatientMedicalSummaryHelper";
import { GrowthChartsCacheData } from "@/Patient/Models/GrowthCharts/GrowthChartsCacheData";
import { DisplayContextEnum } from "@/Patient/Models/MedicalNote/Enum/DisplayContextEnum";
import { MedicalItem } from "@/Patient/Models/MedicalSummary/MedicalItem";
import { MedicalItemVitalSign } from "@/Patient/Models/MedicalSummary/MedicalItemVitalSign";
import { ObstetricalSummary } from "@/Patient/Models/MedicalSummary/ObstetricalSummary";
import { IGrowthChartUiModel } from "@/Patient/Models/UI/Graph/IGrowthChartUiModel";
import { IPatientUIModel } from "@/Patient/Models/UI/IPatientUIModel";
import { MedicalItemViewOrigin } from "@/Patient/Models/UI/MedicalNote/Enums/MedicalItem";
import { ConsultationRequest } from "@/Portfolio/Models/ConsultationRequest";
import { IMedicalRequest } from "@/Portfolio/Models/IMedicalRequest";
import { LabRequest } from "@/Portfolio/Models/LabRequest";
import { ConsultationRequestsInboxItem, RequestsInboxItem } from "@/Portfolio/Models/MyNotesInboxItem";
import { PrintInfo } from "@/Portfolio/Models/PrintInfo";
import { GrowthChartApiClient } from "../Api/GrowthChartApiClient";
import { PatientDocuments } from "./Documents/PatientDocuments";
import { GrowthCharts } from "./GrowthCharts/GrowthCharts";
import { MedicalItemsVitalSignView } from "./MedicalItems/MedicalItemsVitalSign";
import { MedicalNote } from "./MedicalNote/MedicalNote";
import { PatientMedicationTab } from "./MedicationTab/PatientMedicationTab";
import { PatientBanner } from "./PatientBanner/PatientBanner";
import { PatientMergeRequestCancellationController } from "./PatientMergeRequest/PatientMergeRequestCancellationController";
import { PatientMergeRequestController } from "./PatientMergeRequest/PatientMergeRequestController";
import { ProfileView } from "./Profile/ProfileView";
import { ProfileViewControls } from "./Profile/ProfileViewControls";

import styles from "./PatientTab.module.scss";

interface PatientTabProp {
    ActivePatientContactId: Guid;
}

interface PatientTabState {
    growthChartDataModels: GrowthChartsCacheData;
}

class PatientTab extends BaseReactComponent<PatientTabProp, PatientTabState> {
    private patientStoreListenerReference: string;
    private ramqListenerReference: string;

    constructor(props: PatientTabProp) {
        super(props);

        this.state = {
            growthChartDataModels: null
        };
    }

    public async componentDidMount(): Promise<void> {
        $(window).trigger("resize");

        this.patientStoreListenerReference = patientStore.AddChangeListener(this.reRender, this, "patient-tab");
        this.ramqListenerReference = ramqStore.AddChangeListener(this.reRender, this, "ramq-in-patient-tab");

        const growthCharts = await GrowthChartApiClient.getGrowthChartsCacheData();
        this.mutateState(s => s.growthChartDataModels = growthCharts);
    }

    public componentDidUpdate(): void {
        this.updateSecondScreen(this.props.ActivePatientContactId);
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();
        ramqStore.RemoveChangeListener(this.ramqListenerReference);
        patientStore.RemoveChangeListener(this.patientStoreListenerReference);
    }

    // force a re-render
    private reRender(): void {
        this.forceUpdate();
    }

    public render(): JSX.Element {
        if (!this.props?.ActivePatientContactId)
            return null;

        const activeUIModel = patientStore.PatientUIModels[this.props.ActivePatientContactId];
        const activePatientModel = patientStore.PatientModels[this.props.ActivePatientContactId];

        if (!activeUIModel)
            return null;

        const activeVerticalTab = activeUIModel.get("ActiveVerticalTab");
        const activeMedicalItemTab = activeUIModel.get("PatientMedicalItemsActiveTab");
        const canSeePatientMedicalInfo = sessionStore.hasRightsForAction([EmployeeType.Practician]);
        const canSeeDocument = sessionStore.hasPermission(EnumMyleLoginPermission.DocumentTabPermission);
        const medicalNoteModel = patientStore.PatientMedicalNoteModels[this.props.ActivePatientContactId];
        const patientMedicalInfoTabs: JSX.Element[] = [];
        const shouldShowMedicalNoteWasDeletedWarningPopup = activeUIModel.get("ShowEncounterWasDeletedWarning");
        const needUrgentActiveNoteTab = activeUIModel.get("ShowRedNoteActiveTab");
        const patientModel = patientStore.PatientModels[this.props.ActivePatientContactId];
        const isFlowsheetModuleEnabled = cacheStore.ClinicSettings.EnableChronicDiseaseManagement;

        if (canSeePatientMedicalInfo) {
            patientMedicalInfoTabs.push(this.RenderVerticalTab("nav-icon results", MyleEnumsPatientPatientTab.Results, "resultsTab"));
            patientMedicalInfoTabs.push(this.RenderVerticalTab("nav-icon reporting", MyleEnumsPatientPatientTab.Reporting, "reportingTab"));
            patientMedicalInfoTabs.push(this.RenderVerticalTab("nav-icon medication", MyleEnumsPatientPatientTab.Medication, "medicationTab"));
            patientMedicalInfoTabs.push(this.RenderVerticalTab("nav-icon medsummary", MyleEnumsPatientPatientTab.MedicalSummary, "summaryTab"));

            if (medicalNoteModel) {
                patientMedicalInfoTabs.push(this.RenderVerticalTab("nav-icon note", MyleEnumsPatientPatientTab.MedicalNote, "medicalNoteTab", needUrgentActiveNoteTab));
            }

            if (activeUIModel.get("IsPrescriptionTabActive") == true) {
                patientMedicalInfoTabs.push(this.RenderVerticalTab("nav-icon prescription", MyleEnumsPatientPatientTab.Prescription, "prescTab"));
            }

            if (activeMedicalItemTab != null && activeMedicalItemTab != MedicalItemsTab.NONE) {
                patientMedicalInfoTabs.push(this.getMedicalItemsTab());
            }

            if (isFlowsheetModuleEnabled && activeUIModel.get("IsFlowsheetTabActive")) {
                patientMedicalInfoTabs.push(this.RenderVerticalTab("nav-icon flowsheet", MyleEnumsPatientPatientTab.Flowsheet, "flowsheet_tab"));
            }
        }

        return (
            <>
                <div className="myle-page-header bottom-border">
                    {patientStore.PatientBannerModels[this.props.ActivePatientContactId] && (

                        <PatientBanner
                            model={patientStore.PatientBannerModels[this.props.ActivePatientContactId]}
                            patient={patientStore.getCurrentPatient()}
                            patientPortalOverview={patientStore.PatientPortalOverviewModels[patientStore.getCurrentPatientContactId()]}
                            isMSAActive={sessionStore.isMsaActive}
                            onUpdatePatientPortalInfo={(patientContactId, newPortalId, newPortalState, newOverView) => patientStore.updatePatientPortalInfo(patientContactId, newPortalId, newPortalState, newOverView)}
                            isCurrentEmployeeActiveSiteInBritishColumbia={sessionStore.getCurrentEmployee().getActiveSite().isSiteInProvince(CanadaState.BritishColumbia)}
                        />
                    )}
                </div>
                <div>
                    <div
                        className={`fxheight hide-overflow patient-profile ${activeUIModel.get("ActiveVerticalTab") === MyleEnumsPatientPatientTab.History ? styles.historyTab : ""}`}
                        data-cy="patient-tab"
                    >
                        <div className="left">
                            {this.SwitchTabComponents(activeUIModel.get("ActiveVerticalTab"))}
                        </div>
                        <div className="right">
                            <ul className="vnav">
                                {this.RenderVerticalTab("nav-icon appointment", MyleEnumsPatientPatientTab.Appointment, "appointment_tab")}
                                {this.RenderVerticalTab("nav-icon history", MyleEnumsPatientPatientTab.History, "history_tab")}
                                {canSeeDocument && this.RenderVerticalTab("nav-icon documents", MyleEnumsPatientPatientTab.Documents, "documents_tab")}
                                {this.RenderVerticalTab("nav-icon profile", MyleEnumsPatientPatientTab.Profile, "profile_tab")}
                                {patientMedicalInfoTabs}
                            </ul>
                        </div>
                    </div>

                    {shouldShowMedicalNoteWasDeletedWarningPopup &&
                        <MedicalNoteDeletedWarningPopup />
                    }

                </div>
                {this.RenderFooterForView(activeVerticalTab)}

                {medicalNoteModel?.get("ConcurrentEditShowWarningPopup") && <MedicalNoteAlreadyAprovedWarningPopup NoteModel={medicalNoteModel} />}

                {activeUIModel.get("ShowNewExternalMessagePopup") && (
                    <ExternalMessageFormPopupComponent
                        selectedPatient={InternalPatientResponse.fromPerson(Person.FromPatient(activePatientModel))}
                        onClose={() => sessionStore.dispatch(LocalPatientOperations.Profile.ShowAddNewExternalMessagePopup, { value: false })}
                        onSend={() => sessionStore.dispatch(LocalPatientOperations.Profile.ShowAddNewExternalMessagePopup, { value: false })}
                    />
                )}

                {activeUIModel.get("ShowProfileMemoPopup") && <PatientMemoPopup PatientId={this.props.ActivePatientContactId} Model={patientStore.PatientUIModels[this.props.ActivePatientContactId]} />}

                {activeUIModel.get("ShowProfileImagePopup") && (
                    <PatientProfileImagePopup
                        profilePictureModel={patientModel.ProfilePicture}
                        localDispatchContext={this}
                        cancelOperation={LocalPatientOperations.Profile.ImageUpload_Open}
                        uploadCompleteOperation={LocalPatientOperations.Profile.ImageUpload_Completed}
                        deletePicOperation={LocalPatientOperations.Profile.DeleteProfilePic}
                    />
                )}
                {activeUIModel.get("ShowDeactivatePopup") &&
                    <PatientDeactivatePopPopup PatientId={this.props.ActivePatientContactId} Model={patientStore.PatientUIModels[this.props.ActivePatientContactId]} PatientModel={patientStore.PatientModels[this.props.ActivePatientContactId]} />
                }
                {activeUIModel.get("ShowMarkTestPatientPopup") &&
                    <MarkTestPatientPopup PatientId={this.props.ActivePatientContactId} />
                }
                {cacheStore.ClinicSettings.EnableUserPatientAutoMergeRequest && sessionStore.hasPermission(EnumMyleLoginPermission.Administration_PatientMergeRequestPermission) && activeUIModel.get("ShowPatientMergeRequestPopup") && (
                    <PatientMergeRequestController
                        mainPatientContext={patientStore.PatientModels[this.props.ActivePatientContactId]}
                        onClose={() => sessionStore.dispatch(LocalPatientOperations.UI.TogglePatientMergeRequestPopup, { showPopup: false, contactId: this.props.ActivePatientContactId })}
                    />
                )}
                {cacheStore.ClinicSettings.EnableUserPatientAutoMergeRequest && sessionStore.hasPermission(EnumMyleLoginPermission.Administration_PatientMergeRequestPermission) && activeUIModel.get("ShowPatientMergeRequestCancellingPopup") && (
                    <PatientMergeRequestCancellationController
                        currentPatient={patientStore.PatientModels[this.props.ActivePatientContactId]}
                        onClose={() => sessionStore.dispatch(LocalPatientOperations.UI.ToggleCancelPatientMergeRequestPopup, { showPopup: false, contactId: this.props.ActivePatientContactId })}
                    />
                )}
            </>
        );
    }

    private RenderFooterForView(current: MyleEnumsPatientPatientTab): JSX.Element {
        switch (current) {
            case MyleEnumsPatientPatientTab.Prescription:
            case MyleEnumsPatientPatientTab.Appointment:
            case MyleEnumsPatientPatientTab.History:
            case MyleEnumsPatientPatientTab.Documents:
            case MyleEnumsPatientPatientTab.Results:
            case MyleEnumsPatientPatientTab.MedicalSummary:
            case MyleEnumsPatientPatientTab.MedicalItems:
            case MyleEnumsPatientPatientTab.Medication:
            case MyleEnumsPatientPatientTab.MedicalNote:
            case MyleEnumsPatientPatientTab.Reporting:
            case MyleEnumsPatientPatientTab.Flowsheet:
                return this.RenderCollapsedFooter();

            case MyleEnumsPatientPatientTab.Results:
            case MyleEnumsPatientPatientTab.Profile:
            default:
                return (
                    <div className="ws-footer patient-profile">
                        <div className="tb patient-profile-footer">
                            <div className="tbc left">
                                {this.SwitchTabControlComponents(current)}
                            </div>
                            <div className="tbc right">{/* "keep empty" */}</div>
                        </div>
                    </div>
                );
        }
    }

    private RenderCollapsedFooter(): JSX.Element {
        return (<div className="ws-footer collapsed" />);
    }

    private GetActivePatientUIModel(): IPatientUIModel {
        var result: IPatientUIModel = patientStore.PatientUIModels[this.props.ActivePatientContactId];
        return result;
    }

    private onEncounterMyNotesFilterChecked(checked: boolean): void {
        SecondScreenNoteService.getInstance().triggerSecondScreenUpdate(SecondScreenOperation.FilterEncounterMyNotes, { value: checked });
        sessionStore.dispatch(LocalPatientOperations.MedicalSummary.EncounterMyNotesFilterTermChanged, { value: checked });
    }

    private onEncounterUnapprovedNotesFilterChecked(checked: boolean): void {
        SecondScreenNoteService.getInstance().triggerSecondScreenUpdate(SecondScreenOperation.FilterEncounterUnapprovedNotes, { value: checked });
        sessionStore.dispatch(LocalPatientOperations.MedicalSummary.EncounterUnapprovedNotesFilterTermChanged, { value: checked });
    }

    private onEncounterClinicVisitNotesFilterChecked(checked: boolean): void {
        SecondScreenNoteService.getInstance().triggerSecondScreenUpdate(SecondScreenOperation.FilterEncounterClinicVisitNotes, { value: checked });
        sessionStore.dispatch(LocalPatientOperations.MedicalSummary.EncounterClinicVisitNotesFilterTermChanged, { value: checked });
    }

    private getMedicalItemsTab(): JSX.Element {
        let activeMedicalItemTab = patientStore.PatientUIModels[this.props.ActivePatientContactId].get("PatientMedicalItemsActiveTab");
        let css = "";
        let tab = MyleEnumsPatientPatientTab.MedicalItems;
        let keyStr = "";

        switch (activeMedicalItemTab) {
            case MedicalItemsTab.MedicalReactions:
                css = "nav-icon block active ";
                keyStr = "reactions_tab";
                break;
            case MedicalItemsTab.Alerts:
                css = "nav-icon warn active ";
                keyStr = "alerts_tab";
                break;
            case MedicalItemsTab.FamilyHistory:
                css = "nav-icon family active ";
                keyStr = "family_tab";
                break;
            case MedicalItemsTab.Immunization:
                css = "nav-icon vaccination active ";
                keyStr = "vaccination_tab";
                break;
            case MedicalItemsTab.RiskFactors:
                css = "nav-icon drinks active ";
                keyStr = "drinks_tab";
                break;
            case MedicalItemsTab.MedicalProblem:
                css = "nav-icon medical active ";
                keyStr = "medical_tab";
                break;
            case MedicalItemsTab.Medication:
            case MedicalItemsTab.Medication_Edit:
                css = "nav-icon blink active ";
                keyStr = "blink_tab";
                break;
            case MedicalItemsTab.VitalSigns:
                css = "nav-icon vital active ";
                keyStr = "vital_tab";
                break;
            case MedicalItemsTab.ScreeningTest:
                css = "nav-icon screening active ";
                keyStr = "screening_tab";
                break;

            default:
                return null;
        }

        return <li key={keyStr} className={css} onClick={this.ChangePatientTab.bind(this, tab, activeMedicalItemTab)}><a title={this.getHoverTitle(tab, activeMedicalItemTab)} /></li>;
    }

    private SwitchTabComponents(current: MyleEnumsPatientPatientTab): JSX.Element {
        const patientId = this.props.ActivePatientContactId;
        let patientMedicalSummaryModel = patientStore.PatientMedicalSummaryModels[patientId];
        const patientUIModel = patientStore.PatientUIModels[patientId];
        const patientModel = patientStore.PatientModels[patientId];
        const patientAppointmentModel = patientStore.PatientAppointmentModels[patientId];
        const patientMedicationTabModel = patientStore.PatientMedicationTabModels[patientId];
        const patientPrescriptionTabModel = patientStore.PatientPrescriptionTabModels[patientId];
        const patientMedicalNoteModel = patientStore.PatientMedicalNoteModels[patientId];
        const patientProfileCliniciansModel = patientStore.PatientProfileCliniciansModels[patientId];
        const medicalItemsModel = patientStore.PatientMedicalItemsModels[patientId];

        switch (current) {
            case MyleEnumsPatientPatientTab.Profile:
                return <ProfileView Model={patientUIModel} />;

            case MyleEnumsPatientPatientTab.History:
                return <HistoryView patientId={patientId} model={patientUIModel} />;

            case MyleEnumsPatientPatientTab.Appointment:
                return (
                    <Appointment
                        patientId={patientId}
                        Model={patientAppointmentModel}
                        shouldIgnorePublicationRules={patientStore.shouldIgnorePublishingRules}
                    />
                );

            case MyleEnumsPatientPatientTab.Documents:
                return <PatientDocuments patientId={patientId} patient={patientModel} />;

            case MyleEnumsPatientPatientTab.MedicalSummary:
                return (
                    <MedicalSummary
                        model={patientMedicalSummaryModel}
                        currentPatient={patientStore.PatientModels[patientId]}
                        onRequestDeletedSuccess={(requestId, encounterId) => patientStore.onRequestDeletedSuccess(requestId, encounterId)}
                        onConsultationRequestDeletedSuccess={(requestId, encounterId) => patientStore.onConsultationRequestDeletedSuccess(requestId, encounterId)}
                        isPatientNoteDisabled={patientStore.PatientNoteDisabled}
                        onMedicalItemClick={(item, view) => this.onMedicalItemClick(item, view)}
                        onSwitchView={(item, view) => this.onSwitchView(item, view)}
                        onEncounterClick={encounterId => this.onEncounterClick(encounterId)}
                        onActiveMedicationDetailsClick={() => this.onActiveMedicationDetailsClick()}
                        onNewNote={() => this.onNewNote()}
                        onLabRequestSaveSuccess={(model, shouldSaveRelatedEmployee, printInfo) => this.onLabRequestSaveSuccess(model, shouldSaveRelatedEmployee, printInfo)}
                        onLabRequestToggleIsInvalid={(id, isInvalid) => this.onLabRequestToggleIsInvalid(id, isInvalid)}
                        onConsultationRequestSaveSuccess={(model, shouldSaveRelatedEmployee, printInfo) => this.onConsultationRequestSaveSuccess(model, shouldSaveRelatedEmployee, printInfo)}
                        onConsultationRequestToggleIsInvalid={(id, isInvalid) => this.onConsultationRequestToggleIsInvalid(id, isInvalid)}
                        onEncounterDateFromChanged={date => sessionStore.dispatch(LocalPatientOperations.MedicalSummary.EncounterDateFromChanged, { "value": date })}
                        onEncounterDateToChanged={date => sessionStore.dispatch(LocalPatientOperations.MedicalSummary.EncounterDateToChanged, { "value": date })}
                        onChangeEncounterSearchTermChanged={textOrDate => sessionStore.dispatch(LocalPatientOperations.MedicalSummary.EncounterSearchTermChanged, { "value": textOrDate })}
                        onClickCallbackPrintNotes={() => LocalEvent.Dispatch(LocalPatientOperations.MedicalSummary.PrintNotes)}
                        onShowCriteria={() => LocalEvent.Dispatch(LocalPatientOperations.MedicalSummary.EncounterSearchCriteriaDisplayToggle)}
                        onToggleDisplayOnlyUnsigned={() => LocalEvent.Dispatch(LocalPatientOperations.MedicalSummary.ToggleDisplayOnlyUnsigned)}
                        onUpdateScreeningsModelAfterPrintingRequest={(requestId, requestType) => sessionStore.dispatch(
                            LocalPatientOperations.MedicalItems.UpdateScreeningsModelAfterPrintingRequest,
                            { "requestId": requestId, "requestType": requestType }
                        )}
                        OnSortDoneFunction={(operation, itemId, itemIndex, startGroup, endGroup) => this.OnSortDoneFunction(operation, itemId, itemIndex, startGroup, endGroup)}
                        onSaveObstetricalSummary={(obstetricalSummary: ObstetricalSummary) => this.onSaveObstetricalSummary(obstetricalSummary)}
                        isHighlighting
                        summaryConfiguration={MedicalSummaryConfigurationHelper.getSpecificOrDefaultSummaryConfigurationGridLayout()}
                        onEncounterMyNotesFilterChecked={checked => this.onEncounterMyNotesFilterChecked(checked)}
                        onEncounterUnapprovedNotesFilterChecked={checked => this.onEncounterUnapprovedNotesFilterChecked(checked)}
                        onEncounterClinicVisitNotesFilterChecked={checked => this.onEncounterClinicVisitNotesFilterChecked(checked)}
                    />
                );

            case MyleEnumsPatientPatientTab.MedicalItems:
                const activeTab = patientUIModel.get("PatientMedicalItemsActiveTab");

                switch (activeTab) {
                    case MedicalItemsTab.Medication:
                        return (
                            <PatientMedicationTab
                                PatientContactId={patientId}
                                Model={patientMedicationTabModel}
                                MedicalSummaryModel={patientMedicalSummaryModel}
                            />
                        );

                    case MedicalItemsTab.Medication_Edit:
                        return <MedicalItemsActiveMedicationView Model={medicalItemsModel} PatientContactId={patientId} />;

                    case MedicalItemsTab.MedicalReactions:
                        return <MedicalItemsMedicalReactionView Model={medicalItemsModel} PatientContactId={patientId} />;

                    case MedicalItemsTab.RiskFactors:
                        return (
                            <div>
                                <MedicalItemsRiskFactorView
                                    Model={medicalItemsModel}
                                    PatientContactId={this.props.ActivePatientContactId}
                                />
                            </div>
                        );

                    case MedicalItemsTab.Alerts:
                        return <MedicalItemsAlertView Model={medicalItemsModel} PatientContactId={patientId} />;

                    case MedicalItemsTab.Immunization:
                        return <MedicalItemsImmunizationView Model={medicalItemsModel} PatientContactId={patientId} PatientModel={patientModel} />;

                    case MedicalItemsTab.FamilyHistory:
                        return <MedicalItemsFamilyHistoryView Model={medicalItemsModel} PatientContactId={patientId} />;

                    case MedicalItemsTab.VitalSigns:
                        return <MedicalItemsVitalSignView Model={medicalItemsModel} PatientModel={patientModel} PatientContactId={patientId} growthChartsCacheData={this.state.growthChartDataModels} />;

                    case MedicalItemsTab.MedicalProblem:
                        return <MedicalItemsMedicalProblemView Model={medicalItemsModel} PatientContactId={patientId} showFavorites={true} />;

                    case MedicalItemsTab.ScreeningTest:
                        return <MedicalItemsScreeningTestView Model={medicalItemsModel} PatientModel={patientModel} PatientContactId={patientId} PatientMedicalSummaryModel={patientMedicalSummaryModel} />;

                    default:
                        return <BaseMedicalItemsView Model={medicalItemsModel} DetailsTitle="" PatientContactId={patientId} />;
                }

            case MyleEnumsPatientPatientTab.Medication:
                return (
                    <PatientMedicationTab
                        PatientContactId={patientId}
                        Model={patientMedicationTabModel}
                        MedicalSummaryModel={patientMedicalSummaryModel}
                    />
                );

            case MyleEnumsPatientPatientTab.Prescription:
                return (
                    <PatientPrescriptionTab
                        PatientContactId={patientId}
                        Model={patientPrescriptionTabModel}
                        MedicalSummaryModel={patientMedicalSummaryModel}
                        encounterSiteId={patientMedicalNoteModel?.get("Encounter")?.SiteId}
                        onAdjustMedicationPopupOpen={(medicationId: string) =>
                            sessionStore.dispatch(LocalPatientOperations.PrescriptionTab.LeftPaneActiveMed_Adjust, {
                                id: medicationId
                            })
                        }
                        onAddEmptyMedication={() => sessionStore.dispatch(LocalPatientOperations.PrescriptionTab.NewEmptyRx)}
                        onAddSelectedMedication={(item: MedicationSearchListItem) => this.addSearchedMedication(item)}
                        updateGlobalRenewalsNumberChange={(newText: string) =>
                            sessionStore.dispatch(LocalPatientOperations.PrescriptionTab.UpdateAllRenewalsNumberChange, {
                                value: newText
                            })
                        }
                        onExternalSearchMedicationTextChange={(medicationName: string) =>
                            sessionStore.dispatch(
                                LocalPatientOperations.PrescriptionTab.ExternalSearchMedicationTextChange,
                                { text: medicationName }
                            )
                        }
                        toggleAddAsFavorite={() => sessionStore.dispatch(LocalPatientOperations.PrescriptionTab.ToggleAddAsFavorite)}
                        showPatientDetails={cacheStore.ClinicSettings.EnablePrescriptionRenewal}
                    />
                );

            case MyleEnumsPatientPatientTab.Reporting:
                return (
                    <GrowthCharts
                        patient={patientModel}
                        growthChartCache={this.state.growthChartDataModels}
                        vitals={this.getVitalsCollection(patientId)}
                        growthChartUI={this.getGrowthChartsUiState(patientId)}
                        encounters={patientMedicalSummaryModel?.get("Encounters")}
                    />
                );

            case MyleEnumsPatientPatientTab.Results:
                return (
                    <PatientLabsTab
                        patientId={patientId}
                        patient={patientModel}
                        onCloseManualEntryPopup={() => sessionStore.dispatch(LocalPatientOperations.Flowsheets.SetForceReload, { patientId, value: true })}
                    />
                );

            case MyleEnumsPatientPatientTab.MedicalNote:
                return (
                    <MedicalNote
                        PatientUIModel={patientUIModel}
                        ClinicianModel={patientProfileCliniciansModel}
                        PatientId={patientId}
                        NoteModel={patientMedicalNoteModel}
                        MedicalSummaryModel={patientMedicalSummaryModel}
                        PreferredPharmacy={patientModel.PreferredPharmacy.getPharmacyInfo()}
                        PatientModel={patientModel}
                        onEncounterMyNotesFilterChecked={checked => this.onEncounterMyNotesFilterChecked(checked)}
                        onEncounterUnapprovedNotesFilterChecked={checked => this.onEncounterUnapprovedNotesFilterChecked(checked)}
                        onEncounterClinicVisitNotesFilterChecked={checked => this.onEncounterClinicVisitNotesFilterChecked(checked)}
                        isHighlighting
                        isMedicalSummaryCollapsed={sessionStore.toggleMedicalSummarySection}
                        displayContext={DisplayContextEnum.PrimaryDisplay}
                    />
                );
            case MyleEnumsPatientPatientTab.Flowsheet:
                return (
                    <EmployeeContextProvider>
                        <FlowsheetModule
                            metadata={patientUIModel.get("FlowsheetSummary")}
                            patientId={patientModel.IndividualResponse.ContactFacetId}
                            leaveFlowsheet={() => this.CloseFlowsheetTab()}
                        />
                    </EmployeeContextProvider>
                );

            default:
                return <EmptyTab />;
        }
    }

    private getVitalsCollection(patientId: string): Immutable.List<MedicalItemVitalSign> {
        const vitalsDictionary: { [emrTemplateId: string]: MedicalItemVitalSign; } = {};
        const summary = patientStore.PatientMedicalSummaryModels[patientId];
        if (!summary)
            return [].toImmutable();

        const vitals = patientStore.PatientMedicalSummaryModels[patientId].get("MedicalItemsVitalSign");
        if (!vitals)
            return [].toImmutable();

        const sortedVitals = vitals.sortBy(vital => vital.LastUpdateDate);
        sortedVitals.forEach(vital => {
            vitalsDictionary[vital.EMRTemplateId] = vital;
        });

        return Object.values(vitalsDictionary).toImmutable();
    }

    private SwitchTabControlComponents(current: MyleEnumsPatientPatientTab): JSX.Element {
        switch (current) {
            case MyleEnumsPatientPatientTab.Appointment:
                return <div />;
            case MyleEnumsPatientPatientTab.Documents:
                return <div />;
            case MyleEnumsPatientPatientTab.History:
                return <div />;
            case MyleEnumsPatientPatientTab.MedicalSummary:
                return <div />;
            case MyleEnumsPatientPatientTab.Prescription:
                return <div />;
            case MyleEnumsPatientPatientTab.Flowsheet:
                return <div />;
            case MyleEnumsPatientPatientTab.Profile:
                return <ProfileViewControls model={patientStore.PatientUIModels[this.props.ActivePatientContactId]} />;
            case MyleEnumsPatientPatientTab.Reporting:
                return <div />;
            case MyleEnumsPatientPatientTab.Results:
                return <div />;
            default:
                return <div />;
        }
    }

    private getGrowthChartsUiState(patientId: string): IGrowthChartUiModel {
        const graphsInitialization = patientStore.GrowthChartUiModels[patientId];

        if (graphsInitialization == null) {
            const patient = patientStore.PatientModels[this.props.ActivePatientContactId];

            const startDate = patient.IndividualResponse.DateOfBirth;

            const graphsInitializationDefault = {
                startDateFilter: startDate,
                endDateFilter: DateTime.now(),
                unit: sessionStore.useMetricSystemAsDefault ? UnitSystem.Metric : UnitSystem.Imperial
            };

            patientStore.GrowthChartUiModels[this.props.ActivePatientContactId] = Immutable.fromJS(graphsInitializationDefault);
        }

        return patientStore.GrowthChartUiModels[this.props.ActivePatientContactId];
    }

    private getHoverTitle(tab: MyleEnumsPatientPatientTab, subtab: MedicalItemsTab = null): string {
        if (subtab != null && subtab != undefined) {
            switch (subtab) {
                case MedicalItemsTab.Alerts:
                    return cacheStore.GetString("AlertsAndSpecialNeedsLabel");
                case MedicalItemsTab.FamilyHistory:
                    return cacheStore.GetString("FamilyHistoryLabel");
                case MedicalItemsTab.Immunization:
                    return cacheStore.GetString("ImmunizationLabel");
                case MedicalItemsTab.MedicalProblem:
                    return cacheStore.GetString("ProblemsAndDiagnosesLabel");
                case MedicalItemsTab.MedicalReactions:
                    return cacheStore.GetString("AllergiesIntolerancesLabel");
                case MedicalItemsTab.Medication:
                    return cacheStore.GetString("MedicationsLabel");
                case MedicalItemsTab.RiskFactors:
                    return cacheStore.GetString("SocialHabitsLabel");
                case MedicalItemsTab.VitalSigns:
                    return cacheStore.GetString("VitalSignsLabel");
                case MedicalItemsTab.ScreeningTest:
                    return cacheStore.GetString("PreventiveActivityLabel");
            }
        }

        switch (tab) {
            case MyleEnumsPatientPatientTab.Appointment:
                return cacheStore.GetString("AppointmentLabel");
            case MyleEnumsPatientPatientTab.Documents:
                return cacheStore.GetString("ReceivedDocumentsLabel");
            case MyleEnumsPatientPatientTab.History:
                return cacheStore.GetString("HistoryLabel");
            case MyleEnumsPatientPatientTab.MedicalSummary:
                return cacheStore.GetString("CppLabel");
            case MyleEnumsPatientPatientTab.Prescription:
                return cacheStore.GetString("PrescriptionLabel");
            case MyleEnumsPatientPatientTab.Profile:
                return cacheStore.GetString("PatientProfileLabel");
            case MyleEnumsPatientPatientTab.Reporting:
                return cacheStore.GetString("ReportingLabel");
            case MyleEnumsPatientPatientTab.Results:
                return cacheStore.GetString("ResultsLabel");
            case MyleEnumsPatientPatientTab.MedicalNote:
                return cacheStore.GetString("NoteDLabel");
            case MyleEnumsPatientPatientTab.Medication:
                return cacheStore.GetString("MedicationsLabel");
            case MyleEnumsPatientPatientTab.Flowsheet:
                return cacheStore.GetString("Flowsheets_FlowsheetsLabel");
            default:
                return "";
        }
    }

    private RenderVerticalTab(cssClass: string, tab: MyleEnumsPatientPatientTab, keyStr: string, needUrgentStyling: boolean = false): JSX.Element {
        var css = cssClass;
        var medicalItemsTab = null;

        if (this.GetActivePatientUIModel().get("ActiveVerticalTab") === tab) {
            if (tab == MyleEnumsPatientPatientTab.MedicalSummary) {
                var activeMedicalSummaryTab = patientStore.PatientUIModels[this.props.ActivePatientContactId].get("PatientMedicalItemsActiveTab");
                if (activeMedicalSummaryTab == MedicalItemsTab.NONE) {
                    css += " active";
                    medicalItemsTab = MedicalItemsTab.NONE;
                }
            }
            else {
                css += " active";
            }
        }

        const urgentStyling: React.CSSProperties = { background: "#FF28001A", borderLeft: "red solid 3px" };

        return <li key={keyStr} className={css} style={needUrgentStyling ? urgentStyling : {}} onClick={this.ChangePatientTab.bind(this, tab, medicalItemsTab)}><a title={this.getHoverTitle(tab)} data-cy={`patientTab-${MyleEnumsPatientPatientTab[tab].toLowerCase()}`} /></li>;
    }

    private ChangePatientTab(tab: MyleEnumsPatientPatientTab, subtab: MedicalItemsTab = null): void {
        const changeTab = new LocalEvent(LocalPatientOperations.UI.ChangeVerticalTabs);
        changeTab.AddItem("value", tab);

        if (subtab != null || subtab != undefined)
            changeTab.AddItem("medicalitemsTab", subtab);

        LocalEventDispatcher.dispatch(changeTab);
    }

    private async CloseFlowsheetTab(): Promise<void> {
        sessionStore.dispatch(
            LocalPatientOperations.UI.ChangeVerticalTabs,
            {
                value: MyleEnumsPatientPatientTab.MedicalNote,
                isFlowsheetTabActive: false
            }
        );
    }

    private onMedicalItemClick(item: MedicalItem, view: MedicalItemsTab): void {
        PatientMedicalSummaryHelper.setMedicalItemPreviousRoute(view, item?.EMRTemplateMedicalItemSubType);

        sessionStore.dispatch(LocalPatientOperations.MedicalSummary.SwitchToDetails, {
            activeTab: view,
            selectedEMRTemplateId: item?.EMRTemplateId,
            MedicalItemViewOrigin: MedicalItemViewOrigin.Summary,
            PatientId: this.props.ActivePatientContactId,
            isMedicalItemFromOutsideNote: true,
            isCreatingNewMedicalItem: false
        });
    }

    private onSwitchView(item: MedicalItem, view: MedicalItemsTab): void {
        PatientMedicalSummaryHelper.setMedicalItemPreviousRoute(view, item?.EMRTemplateMedicalItemSubType);

        sessionStore.dispatch(LocalPatientOperations.MedicalSummary.SwitchToDetails, {
            activeTab: view,
            MedicalItemViewOrigin: MedicalItemViewOrigin.Summary,
            PatientId: this.props.ActivePatientContactId,
            isMedicalItemFromOutsideNote: true
        });
    }

    private onEncounterClick(encounterId: string): void {
        if (!patientStore.PatientNoteDisabled) {
            const operation = new LocalEvent(LocalPatientOperations.MedicalSummary.EncounterClick);
            operation.AddItem("value", encounterId);
            patientStore.IsMedicalItemFromOutsideNote = false;
            patientStore.IsCreatingNewMedicalItem = false;
            LocalEventDispatcher.dispatch(operation);
        }
    }

    private onActiveMedicationDetailsClick(): void {
        const changeTab = new LocalEvent(LocalPatientOperations.UI.ChangeVerticalTabs);
        changeTab.AddItem("value", PatientTabEnum.Medication);
        changeTab.AddItem("isPrescriptionTabActive", false);
        changeTab.AddItem("MedicationViewOrigin", MedicalItemViewOrigin.Summary);
        LocalEventDispatcher.dispatch(changeTab);
    }

    private onNewNote(): void {
        if (!patientStore.PatientNoteDisabled) {
            sessionStore.dispatch(LocalPatientOperations.MedicalSummary.NewNote);
        }
    }

    private onLabRequestSaveSuccess(model: IMedicalRequest, shouldSaveRelatedEmployee: boolean, printInfo: PrintInfo): void {
        const item = RequestsInboxItem.fromLabRequest(model as LabRequest, printInfo);
        sessionStore.dispatch(LocalPatientOperations.MedicalNote.LabRequestPopup_OnSaveSucces, {
            item: item,
            patientContactId: this.props.ActivePatientContactId,
            shouldSaveRelatedEmployee: shouldSaveRelatedEmployee
        });
    }

    private onLabRequestToggleIsInvalid(id: Guid, isInvalid: boolean): void {
        sessionStore.dispatch(
            LocalPatientOperations.MedicalNote.LabRequestPopup_ToggleIsInvalid,
            {
                requestId: id,
                isInvalid: isInvalid,
                patientContactId: this.props.ActivePatientContactId
            }
        );
    }

    private onConsultationRequestSaveSuccess(model: IMedicalRequest, shouldSaveRelatedEmployee: boolean, printInfo: PrintInfo): void {
        const item = ConsultationRequestsInboxItem.fromConsultationRequest(model as ConsultationRequest, printInfo);
        sessionStore.dispatch(LocalPatientOperations.MedicalNote.ConsultationRequestPopup_OnSaveSucces, {
            item: item,
            patientContactId: this.props.ActivePatientContactId,
            shouldSaveRelatedEmployee: shouldSaveRelatedEmployee
        });
    }

    private onConsultationRequestToggleIsInvalid(id: Guid, isInvalid: boolean): void {
        sessionStore.dispatch(
            LocalPatientOperations.MedicalNote.ConsultationRequestPopup_ToggleIsInvalid,
            {
                consultationRequestId: id,
                isInvalid: isInvalid,
                patientContactId: this.props.ActivePatientContactId
            }
        );
    }

    private OnSortDoneFunction(operation: number, itemId: string, itemIndex: number, startGroup: string, endGroup: string): void {
        const op = new LocalEvent(operation);
        op.AddItem("itemId", itemId);
        op.AddItem("itemIndex", itemIndex);
        op.AddItem("startGroup", startGroup);
        op.AddItem("destinationGroup", endGroup);
        LocalEventDispatcher.dispatch(op);
    }

    private onSaveObstetricalSummary(obstetricalSummary: ObstetricalSummary): void {
        sessionStore.dispatch(
            LocalPatientOperations.MedicalSummary.SaveObstetricalSummary
            ,
            {
                ObstetricalSummary: obstetricalSummary
            }
        );
    }

    private updateSecondScreen(patientId: Guid): void {
        if (SecondScreenNoteService.getInstance().isChildWindowOpen())
            SecondScreenNoteService.getInstance().debounceUpdateModel(new SecondScreenNoteData({ patientId }));
    }

    private addSearchedMedication(item: MedicationSearchListItem): void {
        if (item.isFavorite) {
            const operation = new LocalEvent(LocalPatientOperations.PrescriptionTab.FavoriteMedicationSelected);
            operation.AddItem("item", item.favoriteItem);
            LocalEventDispatcher.dispatch(operation);
        }
        else {
            const operation = new LocalEvent(LocalPatientOperations.PrescriptionTab.MedicationSelected);
            operation.AddItem("item", item.responseItem);
            LocalEventDispatcher.dispatch(operation);
        }
    }
}

export { PatientTab };
